/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GamoraControllerContextIdentifier,
  GamoraFoundationGlueListenerType,
  GamoraFoundationNodeArgs,
  debugCreate,
} from '@ver.id/libs.internal.gamora';
import {
  HoruzProtocolChulakClientStateInvoker,
  HoruzProtocolChulakClientStateInvokerCompletedPresentationInput,
  HoruzProtocolChulakClientStateInvokerWaitingForPresentationInput,
} from './enums.js';
import {
  HoruzProtocolChulakMessageAppPresentationCompletedGuard,
  HoruzProtocolChulakMessageAppPresentationEnterGuard,
  HoruzProtocolChulakMessageAppPresentationExitGuard,
} from '../../guards/message/index.js';
import { HoruzProtocolChulakClientStateBase } from './base.js';
import { HoruzProtocolChulakClientStateContext } from '../context.js';
import { HoruzProtocolChulakClientStateMap } from '../../statemap.js';
import { HoruzProtocolChulakDataType } from '../../data.js';
import { HoruzProtocolChulakErrorProtocolInvalidMessage } from '../../errors/protocol/invalid-message.error.js';
import { IHoruz } from '../../../../horuz.js';
const debug = debugCreate({
  namespace: 'horuz:protocols:chulak:states:client:app-presentation',
});

/**
 * Client
 *
 * Initialization state
 * @public
 */
export class HoruzProtocolChulakClientStateAppPresentation extends HoruzProtocolChulakClientStateBase<
  typeof HoruzProtocolChulakClientStateMap,
  HoruzProtocolChulakClientStateContext,
  IHoruz
> {
  constructor(args: GamoraFoundationNodeArgs) {
    super(args);

    this._onAppPresentationExitMessage =
      this._onAppPresentationExitMessage.bind(this);
    this._onAppPresentationCompletedMessage =
      this._onAppPresentationCompletedMessage.bind(this);
  }

  /**
   * Event on message
   * @param contextIdentifier The context identifier
   */
  private _onQueueNext = async (
    clientStateContext: HoruzProtocolChulakClientStateContext,
  ) => {
    return this.$tryPostTask(clientStateContext, async () => {
      if (this.$hasPostedTransition(clientStateContext)) {
        return;
      }

      try {
        // Execute state
        const result = this.recv(clientStateContext);
        if (result.done === false) {
          // Decode message type
          switch (result.message.type) {
            case HoruzProtocolChulakDataType.DATA_APP_PRESENTATION_COMPLETED: {
              await this._onAppPresentationCompletedMessage(
                clientStateContext,
                result.message,
              );
              break;
            }
            case HoruzProtocolChulakDataType.STATE_APP_PRESENTATION_EXIT: {
              await this._onAppPresentationExitMessage(
                clientStateContext,
                result.message,
              );
              break;
            }
            default: {
              throw HoruzProtocolChulakErrorProtocolInvalidMessage.Create(
                'Message ' +
                  result.message.type +
                  ' not supported in AppPresentation state.',
              );
            }
          }
        }
      } catch (e: any) {
        debug.trace(e?.message, e?.stack);
        this._tryHandleException(clientStateContext, e);
      }
    });
  };

  /**
   * Event
   *
   * Handle
   */
  private async _onAppPresentationCompletedMessage(
    clientStateContext: HoruzProtocolChulakClientStateContext,
    message: HoruzProtocolChulakMessageAppPresentationCompletedGuard,
  ) {
    await this.$executeInvocation(
      clientStateContext.contextIdentifier,
      HoruzProtocolChulakClientStateInvoker.CompletedPresentation,
      clientStateContext,
      {
        compact: message.compact,
      },
    );
  }

  /**
   * Event
   *
   * Handle
   */
  private async _onAppPresentationExitMessage(
    clientStateContext: HoruzProtocolChulakClientStateContext,
    message: HoruzProtocolChulakMessageAppPresentationExitGuard,
  ) {
    this.$postTransition(clientStateContext, { stateNext: 'Done' });
  }

  /**
   * Enter state
   */
  override async $enter(
    clientStateContext: HoruzProtocolChulakClientStateContext,
  ): Promise<void> {
    try {
      await this.$executeInvocation(
        clientStateContext.contextIdentifier,
        HoruzProtocolChulakClientStateInvoker.WaitingForPresentation,
        clientStateContext,
        {} as HoruzProtocolChulakClientStateInvokerWaitingForPresentationInput,
      );

      // Listen for messages
      this.$root
        .$('queues')
        .$('data')
        .$on(clientStateContext.contextIdentifier, 'next', () =>
          this._onQueueNext(clientStateContext),
        );
    } catch (e: any) {
      debug.trace(e?.message, e?.stack);
      this._tryHandleException(clientStateContext, e);
    }
  }

  /**
   * Exit state
   */
  override async $exit(
    clientStateContext: HoruzProtocolChulakClientStateContext,
  ): Promise<void> {
    // Stop listening for messages
    this.$root
      .$('queues')
      .$('data')
      .$removeEventListenersByNamespaceAndName(
        clientStateContext.contextIdentifier,
        'next',
      );
  }

  /**
   * Register invokers
   */
  invoker(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: typeof HoruzProtocolChulakClientStateInvoker.WaitingForPresentation,
    listener: (
      clientStateContext: HoruzProtocolChulakClientStateContext,
      input: HoruzProtocolChulakClientStateInvokerWaitingForPresentationInput,
    ) => Promise<void>,
  ): this;
  invoker(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: typeof HoruzProtocolChulakClientStateInvoker.CompletedPresentation,
    listener: (
      clientStateContext: HoruzProtocolChulakClientStateContext,
      input: HoruzProtocolChulakClientStateInvokerCompletedPresentationInput,
    ) => Promise<void>,
  ): this;
  invoker<K extends keyof typeof HoruzProtocolChulakClientStateInvoker>(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: K,
    listener: GamoraFoundationGlueListenerType,
  ): this {
    return super.$registerInvoker(contextIdentifier, name, listener);
  }
}
